/**
 * JobSemanticBlocks.jsx — client-side render of the two SEO blocks we
 * inject in the SSR template (`ssr/templates.mjs`). Keeping the same
 * copy on both sides means Googlebot sees identical content whether
 * it consumes the initial HTML or the JS-rendered DOM, so we cannot
 * be flagged for cloaking or content-mismatch.
 */
import React from "react";
import { Link } from "react-router-dom";

const SECTOR_LABELS = {
  railway: "Railway", banking: "Banking", ssc: "SSC", upsc: "UPSC",
  defense: "Defense", defence: "Defense", teaching: "Teaching",
  "state-govt": "State Govt", psu: "PSU",
};
const STATE_SLUG_MAP = {
  "uttar pradesh": "uttar-pradesh-government-jobs",
  "bihar": "bihar-government-jobs",
  "maharashtra": "maharashtra-government-jobs",
  "madhya pradesh": "madhya-pradesh-government-jobs",
  "rajasthan": "rajasthan-government-jobs",
  "delhi": "delhi-government-jobs",
  "karnataka": "karnataka-government-jobs",
  "tamil nadu": "tamil-nadu-government-jobs",
  "west bengal": "west-bengal-government-jobs",
  "gujarat": "gujarat-government-jobs",
  "andhra pradesh": "andhra-pradesh-government-jobs",
  "telangana": "telangana-government-jobs",
  "kerala": "kerala-government-jobs",
  "punjab": "punjab-government-jobs",
  "haryana": "haryana-government-jobs",
  "odisha": "odisha-government-jobs",
};
const SIBLING_SECTORS = {
  railway: ["ssc", "psu"], banking: ["ssc", "psu"],
  ssc: ["banking", "railway"], upsc: ["defense", "state-govt"],
  defense: ["upsc", "state-govt"], defence: ["upsc", "state-govt"],
  teaching: ["state-govt", "ssc"], "state-govt": ["upsc", "teaching"],
  psu: ["banking", "railway"],
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}
function fmtNum(n) { return Number(n || 0).toLocaleString("en-IN"); }

export function JobContextBlock({ job }) {
  if (!job) return null;
  const title = job.title || "This vacancy";
  const org = job.organization || "the recruiting authority";
  const posts = Number(job.total_posts) || 0;
  const state = job.state || "";
  const qual = job.qualification || "";
  const dept = (job.department || "").replace(/-/g, " ");
  const ageMin = Number(job.age_min || 0);
  const ageMax = Number(job.age_max || 0);
  const salMin = Number(job.salary_min || 0);
  const salMax = Number(job.salary_max || 0);
  const lastDate = fmtDate(job.last_date);
  const postedDate = fmtDate(job.posted_date);

  const seed = (job.slug || title).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const variant = seed % 6;
  const cycle = "2026 recruitment cycle";
  const opens = [
    <>This vacancy update for <strong>{title}</strong> provides <strong>{posts || "multiple"} active slots</strong> under the <strong>{org}</strong> framework for the {cycle}.</>,
    <><strong>{org}</strong> has opened <strong>{posts || "several"} confirmed posts</strong> in the {cycle} through this <strong>{title}</strong> notification, with applications processed via the official recruitment portal.</>,
    <>The current <strong>{title}</strong> notification lists <strong>{posts || "multiple"} sanctioned openings</strong> across the {org} cadre for the {cycle}, verified against the master notification released by the department.</>,
    <><strong>{title}</strong> is a live {dept ? `${dept} sector` : "government"} recruitment carrying <strong>{posts || "multiple"} approved vacancies</strong> for the {cycle} under <strong>{org}</strong>.</>,
    <>Applications are now open for <strong>{posts || "multiple"}</strong> {title} posts announced by <strong>{org}</strong> as part of the {cycle}{state ? ` — with placement primarily across ${state}` : ""}.</>,
    <>The {cycle} <strong>{title}</strong> notification carries <strong>{posts || "multiple"} vacancies</strong>{state ? ` for ${state}` : ""}, released by <strong>{org}</strong> for direct recruitment through the standard selection process.</>,
  ];

  const midParts = [];
  if (qual) midParts.push(<>Minimum eligibility is <strong>{qual}</strong></>);
  if (ageMin || ageMax) midParts.push(<>the candidate age band is <strong>{ageMin || 18}–{ageMax || 35} years</strong> (with statutory relaxations for reserved categories as per DoPT norms)</>);
  if (salMin || salMax) midParts.push(<>the pay range works out to <strong>₹{fmtNum(salMin)}–₹{fmtNum(salMax || salMin)} per month</strong> including admissible allowances</>);

  return (
    <section data-testid="job-context-block" className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-lg">
      <p className="text-slate-800 leading-relaxed">{opens[variant]}</p>
      {midParts.length > 0 && (
        <p className="text-slate-800 leading-relaxed mt-3">
          {midParts.map((p, i) => (
            <React.Fragment key={i}>{p}{i < midParts.length - 1 ? ", " : "."}</React.Fragment>
          ))}
        </p>
      )}
      {(postedDate || lastDate) && (
        <p className="text-slate-800 leading-relaxed mt-3">
          This notification was
          {postedDate && <> released on <strong>{postedDate}</strong></>}
          {lastDate && <> with the online window closing on <strong>{lastDate}</strong></>}
          {" "}— candidates are advised to complete registration well before the closing timestamp to avoid last-hour portal congestion.
        </p>
      )}
    </section>
  );
}

export function SmartInterlinks({ job = {}, variant = "govt" }) {
  const links = [];
  const seen = new Set();
  const push = (url, label) => { if (!url || seen.has(url)) return; seen.add(url); links.push({ url, label }); };

  if (variant === "private") {
    const co = job.company || {};
    const coSlug = job.company_slug;
    const coName = co.short_name || co.name || coSlug;
    if (coSlug) {
      push(`/private-jobs/${coSlug}`, `All openings at ${coName}`);
      push(`/private-jobs/${coSlug}/salaries`, `${coName} salary insights`);
    }
    push("/private-jobs", "Browse all private-sector jobs");
    push("/vacancy-trends", "Live corporate hiring trends");
    push("/all-india-jobs", "Government job alternatives");
    push("/tools/salary-calculator", "In-hand salary calculator");
    push("/blog", "Career guides & interview tips");
  } else {
    const dept = (job.department || "").toLowerCase();
    if (SECTOR_LABELS[dept]) push(`/sectors/${dept}`, `More ${SECTOR_LABELS[dept]} sector jobs`);
    const stKey = (job.state || "").toLowerCase();
    if (STATE_SLUG_MAP[stKey]) push(`/states/${STATE_SLUG_MAP[stKey]}`, `${job.state} government jobs`);
    push("/all-india-jobs", "All India government vacancies");
    (SIBLING_SECTORS[dept] || ["ssc", "railway"]).forEach((s) => {
      if (SECTOR_LABELS[s]) push(`/sectors/${s}`, `${SECTOR_LABELS[s]} exam notifications`);
    });
    push("/vacancy-trends", "Live vacancy trends & sector data");
    push("/admit-cards", "Latest admit cards");
    push("/results", "Latest exam results");
    push("/latest-jobs", "Latest job notifications this week");
    push("/tools/salary-calculator", "Central pay-scale salary calculator");
  }

  const finalLinks = links.slice(0, 8);
  if (finalLinks.length < 3) return null;
  return (
    <section data-testid="smart-interlinks" className="mt-10 p-5 bg-orange-50 border border-orange-200 rounded-lg" aria-labelledby="related-hubs">
      <h2 id="related-hubs" className="text-lg font-extrabold text-orange-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        Related recruitment hubs &amp; trending pages
      </h2>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 list-none p-0 m-0">
        {finalLinks.map((l) => (
          <li key={l.url}>
            <Link
              to={l.url}
              data-testid={`hub-link-${l.url.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
              className="block px-3 py-2 bg-white border border-orange-200 rounded-md text-sm font-medium text-orange-900 hover:bg-orange-100 hover:border-orange-400 transition-colors"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
